import React from 'react';
import { bool, func, object, string } from 'prop-types';
import { intlShape } from 'react-intl';
import classNames from 'classnames';
import { Form as FinalForm } from 'react-final-form';
import { map, path, pathOr } from 'ramda';

import config from '../../../../config';
import {
  convertMoneyToNumber,
  convertUnitToSubUnit,
  getCurrencySymbol,
  unitDivisor,
  getEstimatedPricePerBooking,
  formatMoney,
} from '../../../../util/currency';
import * as validators from '../../../../util/validators';
import { stringValuesToNumbers } from '../../../../util/data';
import { types as sdkTypes } from '../../../../util/sdkLoader';
import { withCustomerFee } from '../../../../util/pricing';
import { CUSTOMER_COMMISSION_PERCENTAGE, PROVIDER_COMMISSION_PERCENTAGE } from '@swimmy_/helpers';
import {
  FieldNumericStepper,
  FieldStepperEditable,
  Form,
  PrimaryButton,
  Helper,
} from '../../../../components';

import FieldMinAmountPerHour from '../FieldMinAmountPerHour/FieldMinAmountPerHour';
import { getFieldMinAmountProps } from './BasePricingForm.helpers';
import css from './BasePricingForm.module.css';
import IconDown from "../../../IconDown/IconDown";

const { Money } = sdkTypes;

const BasePricingForm = (props) => {
  const { className, intl, isMobile, listing, onSubmit, updated, updateInProgress } = props;
  const unitDivisorValue = unitDivisor(config.currency);

  const handleSubmit = (values) => {
    const valuesAsNumber = stringValuesToNumbers(values);
    const {
      minAmountPerHour = 1,
      minimumHoursPerBooking,
      price,
      minTotalAmountPerBooking = undefined,
    } = valuesAsNumber;
    const priceWithSubUnit = convertUnitToSubUnit(price, unitDivisorValue);
    const publicData = {
      minAmountPerHour: priceWithSubUnit * minAmountPerHour,
      minimumHoursPerBooking: minimumHoursPerBooking || 1,
      minTotalAmountPerBooking: minTotalAmountPerBooking
        ? parseInt(minTotalAmountPerBooking, 10)
        : undefined,
    };
    const updatedValues = {
      price: new Money(priceWithSubUnit, config.currency),
      publicData,
    };
    onSubmit(updatedValues);
  };

  const minPriceValue = config.listingMinimumPriceSubUnits / unitDivisorValue;
  const currencySymbol = getCurrencySymbol(config.currencyConfig);
  const listingPrice = path(['attributes', 'price'], listing);
  const listingPublicData = pathOr({}, ['attributes', 'publicData'], listing);
  const {
    minAmountPerHour,
    minimumHoursPerBooking,
    minTotalAmountPerBooking = 0,
  } = listingPublicData;
  const initialValues = {
    minAmountPerHour: listingPrice
      ? `${minAmountPerHour / convertMoneyToNumber(listingPrice) / unitDivisorValue}`
      : undefined,
    minimumHoursPerBooking: minimumHoursPerBooking || 1,
    price: listingPrice ? `${convertMoneyToNumber(listingPrice)}${currencySymbol}` : undefined,
    minTotalAmountPerBooking:
      minTotalAmountPerBooking > 0 ? `${minTotalAmountPerBooking}${currencySymbol}` : undefined,
  };
  const priceLabelText = intl.formatMessage({
    id: `EditListingPricingPanel.BasePricingForm.priceLabel${isMobile ? '' : 'Desktop'}`,
  });
  const minimumHoursPerBookingLabelText = intl.formatMessage({
    id: 'EditListingPricingPanel.BasePricingForm.minimumHoursPerBookingLabel',
  });
  const submitText = intl.formatMessage({ id: 'Marketplace.save' });
  const minPriceErrorText = intl.formatMessage(
    { id: 'EditListingPricingPanel.BasePricingForm.minPriceError' },
    { amount: minPriceValue, currency: currencySymbol }
  );
  const priceValidators = validators.minNumberAsString(2, minPriceErrorText);
  const minAmountLabelText = intl.formatMessage({
    id: 'EditListingPricingPanel.BasePricingForm.minAmountLabel',
  });
  const minAmountHelperText = intl.formatMessage({
    id: 'EditListingPricingPanel.BasePricingForm.minAmountHelper',
  });
  const classes = classNames(css.root, className);

  return (
    <FinalForm
      initialValues={initialValues}
      onSubmit={handleSubmit}
      render={(formRenderProps) => {
        const { handleSubmit, invalid, pristine, values } = formRenderProps;
        const submitReady = updated && pristine;
        const submitDisabled = invalid || pristine || updateInProgress;
        const valuesAsNumber = stringValuesToNumbers(values);
        const { price, minTotalAmountPerBooking: minTotalAmountPerBookingValue } = valuesAsNumber;
        const priceAsMoney =
          typeof price === 'number' && !isNaN(price)
            ? new Money(convertUnitToSubUnit(price, unitDivisorValue), config.currency)
            : null;
        const guestPriceMoney = priceAsMoney ? withCustomerFee(priceAsMoney) : null;
        const hostEarningsAmount =
          priceAsMoney && typeof PROVIDER_COMMISSION_PERCENTAGE === 'number'
            ? Math.round((priceAsMoney.amount * (100 - PROVIDER_COMMISSION_PERCENTAGE)) / 100)
            : null;
        const hostEarningsMoney =
          hostEarningsAmount != null ? new Money(hostEarningsAmount, config.currency) : null;
        const isPriceValid = !!price && !invalid && !isNaN(price) && price >= minPriceValue;
        const isMinTotalAmountValid =
          !!minTotalAmountPerBookingValue &&
          !isNaN(minTotalAmountPerBookingValue) &&
          minTotalAmountPerBookingValue > 0;

        return (
          <Form className={classes} id="basePricingForm" onSubmit={handleSubmit}>
            <div className={css.inputContainer}>
              <FieldStepperEditable
                className={css.input}
                name="price"
                initialValue={initialValues.price}
                label={priceLabelText}
                min={minPriceValue}
                placeholder={`00${currencySymbol}`}
                unit={currencySymbol}
                validate={priceValidators}
                withButtons={isMobile}
              />
              {isPriceValid && guestPriceMoney && hostEarningsMoney && (
                <details className={css.priceBreakdown}>
                  <summary className={css.priceBreakdownSummary}>
                  <span>
                    {intl.formatMessage(
                      { id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownSummary' },
                      {
                        price: intl.formatMessage(
                          { id: 'Marketplace.priceWithUnit' },
                          { price: formatMoney(intl, guestPriceMoney, false) }
                        ),
                      }
                    )}
                    <IconDown/>
                  </span>
                  </summary>
                  <div className={css.priceBreakdownContent}>
                    <div className={css.priceBreakdownSection}>
                      <div className={css.priceBreakdownSectionTitle}>
                        {intl.formatMessage(
                          {
                            id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownGuestTitle',
                          },
                          {
                            price: formatMoney(intl, guestPriceMoney, false),
                          }
                        )}
                      </div>
                      <ul className={css.priceBreakdownList}>
                        <li>
                          {intl.formatMessage(
                            {
                              id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownBase',
                            },
                            {
                              price: formatMoney(intl, priceAsMoney, false),
                            }
                          )}
                        </li>
                        <li>
                          {intl.formatMessage(
                            {
                              id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownCustomerFee',
                            },
                            {
                              price: formatMoney(
                                intl,
                                new Money(
                                  guestPriceMoney.amount - priceAsMoney.amount,
                                  config.currency
                                ),
                                false
                              ),
                              percentage: CUSTOMER_COMMISSION_PERCENTAGE,
                            }
                          )}
                        </li>
                      </ul>
                    </div>

                    <div className={css.priceBreakdownSection}>
                      <div className={css.priceBreakdownSectionTitle}>
                        {intl.formatMessage(
                          {
                            id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownHostTitle',
                          },
                          {
                            price: formatMoney(intl, hostEarningsMoney, false),
                          }
                        )}
                      </div>
                      <ul className={css.priceBreakdownList}>
                        <li>
                          {intl.formatMessage(
                            {
                              id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownBase',
                            },
                            {
                              price: formatMoney(intl, priceAsMoney, false),
                            }
                          )}
                        </li>
                        <li>
                          {intl.formatMessage(
                            {
                              id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownProviderFee',
                            },
                            {
                              price: formatMoney(
                                intl,
                                new Money(
                                  priceAsMoney.amount - hostEarningsMoney.amount,
                                  config.currency
                                ),
                                false
                              ),
                              percentage: PROVIDER_COMMISSION_PERCENTAGE,
                            }
                          )}
                        </li>
                      </ul>
                    </div>

                    <div className={css.priceBreakdownInfo}>
                      {intl.formatMessage({
                        id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownInfo',
                      })}{' '}
                      <a
                        href="https://help.swimmy.com/hc/fr/articles/360013461819-Quelle-est-la-commission-de-Swimmy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={css.priceBreakdownLink}
                      >
                        {intl.formatMessage({
                          id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownLearnMore',
                        })}
                      </a>
                    </div>
                  </div>
                </details>
              )}
              {getEstimatedPricePerBooking(price) && (
                <Helper
                  className={css.priceHelper}
                  text={intl.formatMessage(
                    { id: 'EditListingPricingPanel.BasePricingForm.estimatedRevenue' },
                    {
                      price: formatMoney(
                        intl,
                        new Money(getEstimatedPricePerBooking(price), config.currency),
                        true
                      ),
                    }
                  )}
                />
              )}
              <FieldNumericStepper
                className={css.input}
                max={8}
                min={1}
                id="minimumHoursPerBooking"
                name="minimumHoursPerBooking"
                label={minimumHoursPerBookingLabelText}
              />
              <FieldStepperEditable
                className={css.input}
                name="minTotalAmountPerBooking"
                initialValue={initialValues.minTotalAmountPerBooking}
                label={intl.formatMessage({
                  id: 'EditListingPricingPanel.BasePricingForm.minTotalAmountPerBooking',
                })}
                min={1}
                placeholder={`00${currencySymbol}`}
                unit={currencySymbol}
                withButtons={isMobile}
              />
              {isMinTotalAmountValid && guestPriceMoney && hostEarningsMoney && (
                <details className={css.priceBreakdown}>
                  <summary className={css.priceBreakdownSummary}>
                    <span>
                      {intl.formatMessage(
                        {
                          id: 'EditListingPricingPanel.BasePricingForm.minTotalPriceBreakdownSummary',
                        },
                        {
                          price: formatMoney(
                            intl,
                            withCustomerFee(
                              new Money(
                                convertUnitToSubUnit(
                                  minTotalAmountPerBookingValue,
                                  unitDivisorValue
                                ),
                                config.currency
                              )
                            ),
                            false
                          )
                        }
                      )}
                      <IconDown/>
                    </span>
                  </summary>
                  <div className={css.priceBreakdownContent}>
                    <div className={css.priceBreakdownSection}>
                      <div className={css.priceBreakdownSectionTitle}>
                        {intl.formatMessage(
                          {
                            id: 'EditListingPricingPanel.BasePricingForm.minTotalPriceBreakdownGuestTitle',
                          },
                          {
                            price: formatMoney(
                              intl,
                              withCustomerFee(
                                new Money(
                                  convertUnitToSubUnit(
                                    minTotalAmountPerBookingValue,
                                    unitDivisorValue
                                  ),
                                  config.currency
                                )
                              ),
                              false
                            ),
                          }
                        )}
                      </div>
                      <ul className={css.priceBreakdownList}>
                        <li>
                          {intl.formatMessage(
                            {
                              id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownBase',
                            },
                            {
                              price: formatMoney(
                                intl,
                                new Money(
                                  convertUnitToSubUnit(
                                    minTotalAmountPerBookingValue,
                                    unitDivisorValue
                                  ),
                                  config.currency
                                ),
                                false
                              ),
                            }
                          )}
                        </li>
                        <li>
                          {(() => {
                            const baseMoney = new Money(
                              convertUnitToSubUnit(minTotalAmountPerBookingValue, unitDivisorValue),
                              config.currency
                            );
                            const guestMoney = withCustomerFee(baseMoney);
                            const feeMoney = new Money(
                              guestMoney.amount - baseMoney.amount,
                              config.currency
                            );
                            return intl.formatMessage(
                              {
                                id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownCustomerFee',
                              },
                              {
                                price: formatMoney(intl, feeMoney, false),
                                percentage: CUSTOMER_COMMISSION_PERCENTAGE,
                              }
                            );
                          })()}
                        </li>
                      </ul>
                    </div>

                    <div className={css.priceBreakdownSection}>
                      <div className={css.priceBreakdownSectionTitle}>
                        {(() => {
                          const baseMoney = new Money(
                            convertUnitToSubUnit(minTotalAmountPerBookingValue, unitDivisorValue),
                            config.currency
                          );
                          const hostAmount = Math.round(
                            (baseMoney.amount * (100 - PROVIDER_COMMISSION_PERCENTAGE)) / 100
                          );
                          const hostMoney = new Money(hostAmount, config.currency);
                          return intl.formatMessage(
                            {
                              id: 'EditListingPricingPanel.BasePricingForm.minTotalPriceBreakdownHostTitle',
                            },
                            {
                              price: formatMoney(intl, hostMoney, false),
                            }
                          );
                        })()}
                      </div>
                      <ul className={css.priceBreakdownList}>
                        <li>
                          {intl.formatMessage(
                            {
                              id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownBase',
                            },
                            {
                              price: formatMoney(
                                intl,
                                new Money(
                                  convertUnitToSubUnit(
                                    minTotalAmountPerBookingValue,
                                    unitDivisorValue
                                  ),
                                  config.currency
                                ),
                                false
                              ),
                            }
                          )}
                        </li>
                        <li>
                          {(() => {
                            const baseMoney = new Money(
                              convertUnitToSubUnit(minTotalAmountPerBookingValue, unitDivisorValue),
                              config.currency
                            );
                            const hostAmount = Math.round(
                              (baseMoney.amount * (100 - PROVIDER_COMMISSION_PERCENTAGE)) / 100
                            );
                            const hostMoney = new Money(hostAmount, config.currency);
                            const feeMoney = new Money(
                              baseMoney.amount - hostMoney.amount,
                              config.currency
                            );
                            return intl.formatMessage(
                              {
                                id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownProviderFee',
                              },
                              {
                                price: formatMoney(intl, feeMoney, false),
                                percentage: PROVIDER_COMMISSION_PERCENTAGE,
                              }
                            );
                          })()}
                        </li>
                      </ul>
                    </div>

                    <div className={css.priceBreakdownInfo}>
                      {intl.formatMessage({
                        id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownInfo',
                      })}{' '}
                      <a
                        href="https://help.swimmy.com/hc/fr/articles/360013461819-Quelle-est-la-commission-de-Swimmy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={css.priceBreakdownLink}
                      >
                        {intl.formatMessage({
                          id: 'EditListingPricingPanel.BasePricingForm.priceBreakdownLearnMore',
                        })}
                      </a>
                    </div>
                  </div>
                </details>
              )}
              {/*<div className={css.minAmountContainer}>*/}
              {/*  <span className={css.minAmountLabel}>{minAmountLabelText}</span>*/}
              {/*  <div className={css.fieldMinAmountWrapper}>*/}
              {/*    {listingPrice &&  map(props => (*/}
              {/*      <FieldMinAmountPerHour*/}
              {/*        key={props.id}*/}
              {/*        className={css.fieldMinAmount}*/}
              {/*        name="minAmountPerHour"*/}
              {/*        {...props}*/}
              {/*      />*/}
              {/*    ), getFieldMinAmountProps(intl, listingPrice.currency, values))}*/}
              {/*  </div>*/}
              {/*  <span className={css.minAmountHelper}>{minAmountHelperText}</span>*/}
              {/*</div>*/}
            </div>
            <div className={css.buttonContainer}>
              <PrimaryButton
                className={css.submitButton}
                type="submit"
                form="basePricingForm"
                inProgress={updateInProgress}
                disabled={submitDisabled}
                ready={submitReady}
              >
                <span>{submitText}</span>
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

BasePricingForm.defaultProps = {
  className: null,
  listing: null,
};

BasePricingForm.propTypes = {
  className: string,
  intl: intlShape.isRequired,
  isMobile: bool.isRequired,
  listing: object,
  onSubmit: func.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
};

export default BasePricingForm;
